// 划词批注的公共交互 —— 方案正文（DocumentEditor）和章节提示词（PromptPane）用的是同一套：
// 渲染 markdown → 选中文字弹出输入框 → 右侧列出批注、点高亮跳转。
// 两边唯一的差别是批注存在哪、谁能删，所以这里只收 props，不碰接口。

import type { MouseEvent, ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { marked } from "marked";
import { Check, MessageSquarePlus, Trash2 } from "lucide-react";

/** 两种批注（文档批注 / 提示词批注）共有的最小字段。`quote_index` 缺省当 0 处理——
 *  提示词批注目前不落这个字段，行为等同于历史上没有该字段的文档批注。 */
export interface AnnotationLike {
  id: number;
  quote: string;
  comment: string;
  resolved: number;
  quote_index?: number;
}

/** 统计 `needle` 在 `haystack` 中出现的次数（不重叠地从左往右数）。 */
function countOccurrences(haystack: string, needle: string): number {
  if (!needle) return 0;
  let count = 0;
  let from = 0;
  while (true) {
    const idx = haystack.indexOf(needle, from);
    if (idx === -1) break;
    count++;
    from = idx + needle.length;
  }
  return count;
}

/** 找出 `quote` 在 `text` 中所有（不重叠的）出现位置，按出现顺序排列。 */
function findOccurrences(text: string, quote: string): number[] {
  const hits: number[] = [];
  let from = 0;
  while (true) {
    const idx = text.indexOf(quote, from);
    if (idx === -1) break;
    hits.push(idx);
    from = idx + quote.length;
  }
  return hits;
}

type TextSegment = { node: Text; start: number; end: number };

/** 把一段解析后的 HTML 按文档顺序展开成纯文本，并记录每个文本节点在这份纯文本里
 *  对应的起止位置。`<br>` 会补一个 "\n"，和浏览器选区 `toString()` 的行为保持一致
 *  （比如表头里 `列名A<br>列名B` 选中后得到 "列名A\n列名B"），但它本身不属于任何
 *  文本节点，纯粹是为了让这里算出的纯文本和选区文本能对得上。 */
function buildPlainTextMap(root: Node): { text: string; segments: TextSegment[] } {
  let text = "";
  const segments: TextSegment[] = [];
  function walk(node: Node) {
    if (node.nodeType === Node.TEXT_NODE) {
      const start = text.length;
      text += node.nodeValue ?? "";
      segments.push({ node: node as Text, start, end: text.length });
      return;
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      if ((node as Element).tagName === "BR") {
        text += "\n";
        return;
      }
      node.childNodes.forEach(walk);
    }
  }
  walk(root);
  return { text, segments };
}

/** 把批注高亮成 <mark> 标签。
 *
 *  批注引用的是预览里选中的纯文本，但这里要匹配的是 marked 渲染出的 HTML —— 同一段
 *  纯文本中间完全可能被 `<strong>`、`<br>` 等标签隔断，比如整行"加粗+不加粗"混排的
 *  段落，或者表头里用 `<br>` 换行的列名。直接对 HTML 字符串做 `indexOf` 会因为中间
 *  多出的标签而永远匹配不到，批注也就不会在预览里显示成高亮。所以这里先把 HTML 解析
 *  成 DOM，按文档顺序拼出一份纯文本（规则上和浏览器选区 `toString()` 保持一致）用来
 *  定位，命中后再把纯文本区间映射回具体的文本节点，按节点切分、逐段包上 `<mark>`——
 *  一条批注跨越多个标签时会拆成多个 `<mark>`，视觉上仍然是连续的高亮。
 *
 *  同一段引用文本（如"首次""参与者"这类常见词）在文档里可能出现多次，
 *  所以不能简单地对每条批注做一次查找 —— 那样会导致所有同文本的批注
 *  全部落到第一次出现的位置上、互相嵌套覆盖，其余真正出现的地方反而没有标记。
 *  这里先枚举每段引用文本在文档中的全部出现位置，再按批注创建时记录的
 *  `quote_index`（第几次出现）分配到对应位置；没有该信息（默认 0）时，
 *  顺延到下一个尚未被占用的出现位置，保证不同批注不会挤在同一处。 */
function highlightQuotes<T extends AnnotationLike>(html: string, annotations: T[]): string {
  const active = annotations.filter((a) => !a.resolved && a.quote.trim());
  if (!active.length) return html;

  const container = document.createElement("div");
  container.innerHTML = html;
  const { text, segments } = buildPlainTextMap(container);

  const occurrenceCache = new Map<string, number[]>();
  function occurrencesOf(quote: string): number[] {
    let hits = occurrenceCache.get(quote);
    if (!hits) {
      hits = findOccurrences(text, quote);
      occurrenceCache.set(quote, hits);
    }
    return hits;
  }

  const usedByQuote = new Map<string, Set<number>>();
  const placements: { start: number; end: number; id: number }[] = [];
  for (const a of active) {
    const quote = a.quote.trim();
    const hits = occurrencesOf(quote);
    if (!hits.length) continue;
    const used = usedByQuote.get(quote) ?? new Set<number>();
    usedByQuote.set(quote, used);
    let pos = Math.min(Math.max(a.quote_index ?? 0, 0), hits.length - 1);
    while (used.has(pos) && used.size < hits.length) {
      pos = (pos + 1) % hits.length;
    }
    used.add(pos);
    const start = hits[pos]!;
    placements.push({ start, end: start + quote.length, id: a.id });
  }

  placements.sort((x, y) => x.start - y.start);
  // 好几条批注完全可能标的是同一处文字（比如同一段话被反复提交了好几条批注，
  // quote 和 quote_index 都一样，算出来的起止位置也就完全相同）——这种严格重合
  // 的情况归并到同一个 <mark> 上，让它同时带上这些批注的 id，而不是像真正的
  // 重叠（理论上不应发生）那样直接丢弃后面的，否则那些批注在侧栏里点了也定位
  // 不到，因为 DOM 里根本没有它们对应的标记。
  const kept: { start: number; end: number; ids: number[] }[] = [];
  let cursor = 0;
  for (const p of placements) {
    if (p.start < cursor) {
      const prev = kept[kept.length - 1];
      if (prev && p.start === prev.start && p.end === prev.end) prev.ids.push(p.id);
      continue;
    }
    kept.push({ start: p.start, end: p.end, ids: [p.id] });
    cursor = p.end;
  }

  // 从后往前处理：给某个文本节点切分只会影响它自身及排在它后面的内容，
  // 不会打乱排在前面、还未处理的批注所记录的纯文本位置。
  for (let i = kept.length - 1; i >= 0; i--) {
    const p = kept[i]!;
    for (const seg of segments) {
      const overlapStart = Math.max(p.start, seg.start);
      const overlapEnd = Math.min(p.end, seg.end);
      if (overlapStart >= overlapEnd) continue;
      const node = seg.node;
      if (!node.parentNode) continue;
      const localStart = overlapStart - seg.start;
      const localEnd = overlapEnd - seg.start;
      if (localEnd < node.length) node.splitText(localEnd);
      const markNode = localStart > 0 ? node.splitText(localStart) : node;
      const mark = document.createElement("mark");
      mark.className = "doc-mark";
      // 空格分隔而不是逗号，方便用 CSS 的 [attr~=value] 按单个 id 精确匹配。
      mark.dataset.annoId = p.ids.join(" ");
      markNode.parentNode!.insertBefore(mark, markNode);
      mark.appendChild(markNode);
    }
  }

  return container.innerHTML;
}

/** 预览区：渲染后的 markdown + 选中文字后的批注输入浮层。
 *
 *  `scrollToId` 由外层（DocumentEditor / PromptPane）在点击右侧批注条目时设置，
 *  用来把左侧正文滚动到对应的高亮处；滚完通过 `onScrolledTo` 把它清空，避免
 *  正文重新渲染（比如切了别的批注）时又滚一次。 */
export function AnnotatedMarkdown<T extends AnnotationLike>({
  content,
  annotations,
  onAdd,
  onActivate,
  scrollToId,
  onScrolledTo,
}: {
  content: string;
  annotations: T[];
  onAdd: (quote: string, comment: string, quoteIndex: number) => Promise<void>;
  onActivate: (id: number) => void;
  scrollToId?: number | null;
  onScrolledTo?: () => void;
}) {
  const [selection, setSelection] = useState<{
    text: string;
    x: number;
    y: number;
    placement: "above" | "below";
    quoteIndex: number;
  } | null>(null);
  const [commentDraft, setCommentDraft] = useState("");
  const previewRef = useRef<HTMLDivElement>(null);

  const html = useMemo(() => {
    const raw = marked.parse(content, { async: false }) as string;
    return highlightQuotes(raw, annotations);
  }, [content, annotations]);

  useEffect(() => {
    if (scrollToId == null) return;
    // `~=` 按空格分隔的单个词匹配，能找到同一处文字上携带这个 id 的共享 mark
    // （哪怕它不是这个 mark 里排最前的那条批注）。
    const mark = previewRef.current?.querySelector<HTMLElement>(`.doc-mark[data-anno-id~="${scrollToId}"]`);
    // 已解决的批注不会渲染高亮，这里找不到 mark 就什么也不做——右侧列表本身已经是选中状态了。
    mark?.scrollIntoView({ behavior: "smooth", block: "center" });
    onScrolledTo?.();
  }, [scrollToId, html]);

  function handleMouseUp() {
    const sel = window.getSelection();
    const text = sel?.toString().trim() ?? "";
    if (!text || !previewRef.current || !sel || sel.rangeCount === 0) {
      setSelection(null);
      return;
    }
    const range = sel.getRangeAt(0);
    if (!previewRef.current.contains(range.commonAncestorContainer)) {
      setSelection(null);
      return;
    }
    // 选区之前的正文里已经出现过几次同样的文字，用来记下这次选中的是第几次出现，
    // 避免同一段文字重复出现时批注高亮全部叠在第一次出现的位置上。
    const preRange = document.createRange();
    preRange.selectNodeContents(previewRef.current);
    preRange.setEnd(range.startContainer, range.startOffset);
    const quoteIndex = countOccurrences(preRange.toString(), text);
    const rect = range.getBoundingClientRect();
    const hostRect = previewRef.current.getBoundingClientRect();
    const POPOVER_WIDTH = 240;
    const POPOVER_HEIGHT = 130;
    const MARGIN = 12;
    const minX = POPOVER_WIDTH / 2 + MARGIN;
    const maxX = Math.max(minX, hostRect.width - POPOVER_WIDTH / 2 - MARGIN);
    const x = Math.min(Math.max(rect.left - hostRect.left + rect.width / 2, minX), maxX);
    const spaceAbove = rect.top - hostRect.top;
    const placement = spaceAbove < POPOVER_HEIGHT + MARGIN ? "below" : "above";
    const y = placement === "below" ? rect.bottom - hostRect.top : rect.top - hostRect.top;
    setSelection({ text, x, y, placement, quoteIndex });
    setCommentDraft("");
  }

  async function submit() {
    if (!selection || !commentDraft.trim()) return;
    await onAdd(selection.text, commentDraft.trim(), selection.quoteIndex);
    setSelection(null);
    setCommentDraft("");
    window.getSelection()?.removeAllRanges();
  }

  function handleClick(e: MouseEvent<HTMLDivElement>) {
    const target = (e.target as HTMLElement).closest<HTMLElement>(".doc-mark");
    if (!target) return;
    // 好几条批注标在完全同一处文字时，这个 mark 会用空格分隔携带它们全部的 id
    // （见 highlightQuotes）；点击正文时定位到其中最早的一条。
    const id = Number((target.dataset.annoId ?? "").split(/\s+/)[0]);
    if (Number.isNaN(id)) return;
    onActivate(id);
    document.querySelector(`[data-anno-item="${id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  return (
    <div className="doc-preview-wrap">
      <div
        className="doc-preview markdown-body"
        ref={previewRef}
        onMouseUp={handleMouseUp}
        onClick={handleClick}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {selection && (
        <div
          className={`doc-anno-popover doc-anno-popover-${selection.placement}`}
          style={{ left: selection.x, top: selection.y }}
        >
          <p className="doc-anno-popover-quote">“{selection.text.slice(0, 60)}”</p>
          <textarea
            className="doc-anno-popover-input"
            rows={2}
            autoFocus
            placeholder="输入批注内容…"
            value={commentDraft}
            onChange={(e) => setCommentDraft(e.target.value)}
          />
          <div className="doc-anno-popover-actions">
            <button className="btn btn-text" type="button" onClick={() => setSelection(null)}>
              取消
            </button>
            <button className="btn btn-clay" type="button" disabled={!commentDraft.trim()} onClick={submit}>
              <MessageSquarePlus size={13} className="icon" />
              <span>添加批注</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** 右侧批注列表。`meta` 用来在批注下补一行来源信息（提示词批注要显示是谁批的）；
 *  `onJump` 用来点击条目时反向定位到左侧正文里对应的高亮处。 */
export function AnnotationSidebar<T extends AnnotationLike>({
  title,
  annotations,
  activeAnnoId,
  emptyHint,
  meta,
  canDelete,
  onToggleResolved,
  onDelete,
  onJump,
}: {
  title: string;
  annotations: T[];
  activeAnnoId: number | null;
  emptyHint: string;
  meta?: (a: T) => ReactNode;
  canDelete?: (a: T) => boolean;
  onToggleResolved: (a: T) => void;
  onDelete: (a: T) => void;
  onJump?: (a: T) => void;
}) {
  return (
    <aside className="doc-editor-sidebar">
      <h3 className="sidebar-title">
        {title}（{annotations.length}）
      </h3>
      {annotations.length === 0 && <p className="admin-empty">{emptyHint}</p>}
      <ul className="doc-anno-list">
        {annotations.map((a) => (
          <li
            className={`doc-anno-item ${a.resolved ? "resolved" : ""} ${activeAnnoId === a.id ? "active" : ""}`}
            data-anno-item={a.id}
            key={a.id}
            onClick={onJump ? () => onJump(a) : undefined}
            title={onJump ? "点击定位到正文中的位置" : undefined}
          >
            <p className="doc-anno-quote">“{a.quote.slice(0, 80)}”</p>
            <p className="doc-anno-comment">{a.comment}</p>
            {meta && <p className="doc-anno-meta">{meta(a)}</p>}
            <div className="doc-anno-item-actions">
              <button
                className="btn btn-text"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleResolved(a);
                }}
              >
                <Check size={12} className="icon" />
                <span>{a.resolved ? "取消已解决" : "标记已解决"}</span>
              </button>
              {(!canDelete || canDelete(a)) && (
                <button
                  className="btn btn-text"
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(a);
                  }}
                >
                  <Trash2 size={12} className="icon" />
                  <span>删除</span>
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </aside>
  );
}
